/**
 * OmegaHealthPage — OMEGA Self-Health dashboard (Monitor watching OMEGA herself).
 *
 * Founder-only. Polls `GET /omega/health` (the S55 liveness panel) and shows the
 * rate + staleness + gate-state signals the old 3σ monitor could not see: is
 * learning ON, how many atoms were created last hour / 24h, how many verticals
 * and conductors are active, when the last external fetch was — plus every
 * liveness alert. The proof panel shows that each Monitor finding is a real
 * signed GI atom (Ed25519 signature, one of the 8 Holosynthics kinds, tagged in
 * atom_tags.db, converged on one conductor) — not a number in memory.
 *
 * Matches the OMEGA white-theme page shell (see OmegaMarketResearchPage).
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types (mirror the /omega/health JSON) ────────────────────────────────────

interface HealthAlert {
  key: string;
  level: 'ok' | 'warn' | 'critical';
  title: string;
  detail: string;
}

interface OmegaHealth {
  learning_status: string;
  atoms_last_hour: number;
  atoms_last_24h: number;
  atoms_total: number;
  active_verticals: number;
  running_conductors: number;
  last_atom_age_secs: number | null;
  last_external_fetch_age_secs: number | null;
  overall: string;
  alerts: HealthAlert[];
}

interface MonitorAtom {
  id: string;
  atom_kind: string;
  layer: string;
  vertical: string;
  payload: string;
  signature_hex: string;
  public_key_hex: string;
  created_iso: string;
}

interface HealthProof {
  monitor_vertical: string;
  conductor_id: string;
  atoms_converged: number;
  atoms_stored_total: number;
  recent_atoms: MonitorAtom[];
  note: string;
}

interface OmegaHealthResponse {
  health: OmegaHealth;
  proof: HealthProof;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function useHealth() {
  return useQuery({
    queryKey: ['omega-health'],
    queryFn: () => apiFetch<OmegaHealthResponse>('/omega/health'),
    refetchInterval: 30_000, // re-poll every 30s; the monitor itself ticks every 5m
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtAge(secs: number | null): string {
  if (secs === null || secs === undefined) return 'never';
  if (secs < 90) return `${secs}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function fmtNum(n: number): string {
  return n.toLocaleString();
}

function statusColor(s: string): string {
  if (s === 'on' || s === 'healthy') return '#16a34a';
  if (s === 'degraded' || s === 'stalled') return '#d97706';
  return '#dc2626'; // off / critical
}

function levelColor(level: string): string {
  if (level === 'critical') return '#dc2626';
  if (level === 'warn') return '#d97706';
  return '#16a34a';
}

function short(hex: string, n = 16): string {
  return hex.length > n ? `${hex.slice(0, n)}…` : hex;
}

// ── Small presentational bits ────────────────────────────────────────────────

function Metric({ label, value, accent }: { label: string; value: string; accent?: string | undefined }) {
  return (
    <div
      style={{
        flex: '1 1 150px',
        minWidth: 140,
        background: 'var(--omega-surface, rgba(0,0,0,0.02))',
        border: '1px solid var(--omega-rim, rgba(0,0,0,0.08))',
        borderRadius: 8,
        padding: '12px 14px',
      }}
    >
      <div style={{ fontSize: '8pt', color: 'var(--omega-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '16pt', fontWeight: 700, marginTop: 4, color: accent ?? 'var(--omega-ink)' }}>
        {value}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaHealthPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const q = useHealth();
  const health = q.data?.health;
  const proof = q.data?.proof;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Self-Health' }]} />

      <div style={{ marginTop: 8, marginBottom: 6, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          OMEGA Self-Health
        </h1>
        {health && (
          <span
            style={{
              fontSize: '9pt',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#fff',
              background: statusColor(health.overall),
              padding: '2px 10px',
              borderRadius: 999,
            }}
          >
            {health.overall}
          </span>
        )}
      </div>
      <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginBottom: 14 }}>
        Monitor watching OMEGA herself — is she learning, are the verticals working, and is every
        finding a real signed atom. Auto-refreshes every 30s.
      </div>

      {q.isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {q.isError && <div style={{ color: '#dc2626', fontSize: '9pt' }}>Failed to load OMEGA health.</div>}

      {health && (
        <>
          {/* Metrics */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Metric label="Learning" value={health.learning_status} accent={statusColor(health.learning_status)} />
            <Metric label="Atoms / hour" value={fmtNum(health.atoms_last_hour)} />
            <Metric label="Atoms / 24h" value={fmtNum(health.atoms_last_24h)} />
            <Metric label="Atoms total" value={fmtNum(health.atoms_total)} />
            <Metric label="Active verticals" value={fmtNum(health.active_verticals)} accent={health.active_verticals === 0 ? '#dc2626' : undefined} />
            <Metric label="Running conductors" value={fmtNum(health.running_conductors)} accent={health.running_conductors === 0 ? '#dc2626' : undefined} />
            <Metric label="Last atom" value={fmtAge(health.last_atom_age_secs)} />
            <Metric label="Last external fetch" value={fmtAge(health.last_external_fetch_age_secs)} />
          </div>

          {/* Alerts */}
          <h2 style={{ fontSize: '10pt', fontWeight: 700, margin: '8px 0' }}>
            Alerts ({health.alerts.length})
          </h2>
          {health.alerts.length === 0 ? (
            <div style={{ fontSize: '9pt', color: '#16a34a' }}>✓ All clear — nothing stalled.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {health.alerts.map((a) => (
                <div
                  key={a.key}
                  style={{
                    borderLeft: `4px solid ${levelColor(a.level)}`,
                    background: 'var(--omega-surface, rgba(0,0,0,0.02))',
                    border: '1px solid var(--omega-rim, rgba(0,0,0,0.08))',
                    borderRadius: 6,
                    padding: '8px 12px',
                  }}
                >
                  <div style={{ fontSize: '9.5pt', fontWeight: 700 }}>
                    <span style={{ color: levelColor(a.level), textTransform: 'uppercase', fontSize: '8pt', marginRight: 8 }}>
                      {a.level}
                    </span>
                    {a.title}
                  </div>
                  <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-muted)', marginTop: 2 }}>{a.detail}</div>
                </div>
              ))}
            </div>
          )}

          {/* Proof — every finding is a real signed GI atom */}
          {proof && (
            <div
              style={{
                marginTop: 10,
                background: 'var(--omega-surface, rgba(0,0,0,0.02))',
                border: '1px solid var(--omega-rim, rgba(0,0,0,0.08))',
                borderRadius: 8,
                padding: '14px 16px',
              }}
            >
              <h2 style={{ fontSize: '10pt', fontWeight: 700, margin: '0 0 4px' }}>Proof — Monitor is a GI</h2>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-muted)', marginBottom: 10 }}>
                {proof.note}
              </div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '9pt', marginBottom: 10 }}>
                <div><strong>Conductor:</strong> <code style={{ fontSize: '8.5pt' }}>{proof.conductor_id}</code></div>
                <div><strong>Atoms converged:</strong> {fmtNum(proof.atoms_converged)}</div>
                <div><strong>Atoms stored:</strong> {fmtNum(proof.atoms_stored_total)}</div>
                <div><strong>Vertical:</strong> <code style={{ fontSize: '8.5pt' }}>{proof.monitor_vertical}</code></div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '8.5pt', width: '100%' }}>
                  <thead>
                    <tr>
                      {['Type', 'Atom id', 'Signature (Ed25519)', 'Created'].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '6px 10px',
                            borderBottom: '2px solid var(--omega-rim, rgba(0,0,0,0.15))',
                            fontWeight: 700,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {proof.recent_atoms.map((atom) => (
                      <tr key={atom.id}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--omega-rim, rgba(0,0,0,0.08))' }}>
                          <span
                            style={{
                              fontWeight: 700,
                              padding: '1px 7px',
                              borderRadius: 999,
                              fontSize: '8pt',
                              background: 'rgba(37,99,235,0.12)',
                              color: '#2563eb',
                            }}
                          >
                            {atom.atom_kind}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--omega-rim, rgba(0,0,0,0.08))', fontFamily: 'monospace' }}>
                          {short(atom.id, 20)}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--omega-rim, rgba(0,0,0,0.08))', fontFamily: 'monospace' }}>
                          {short(atom.signature_hex, 24)}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--omega-rim, rgba(0,0,0,0.08))', whiteSpace: 'nowrap' }}>
                          {atom.created_iso.replace('T', ' ').slice(0, 19)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 18 }}>
        <Link to="/omega" style={{ color: '#2563eb', fontSize: '8.5pt' }}>
          ← OMEGA
        </Link>
      </div>
    </div>
  );
}
